// This component is pretty hacky, and could use some TLC.

import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import VisuallyHidden from '@/components/VisuallyHidden';

import useDrag from './use-drag.hook';

const SPLIT_BREAKPOINT = 700 / 16;

const DIVIDER_WIDTH = 16;

interface Props {
  className?: string;
  splitRatio?: number;
  isFullscreened?: boolean;
  maxHeight?: number | string;
  children: [React.ReactNode, React.ReactNode];
}

function SplitPane({
  className = '',
  splitRatio = 0.5,
  isFullscreened = false,
  maxHeight,
  children,
}: Props) {
  const rulerRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const dividerRef = React.useRef(null);

  let { leftWidth, rightWidth } = useDrag({
    defaultRatio: splitRatio,
    rulerRef: rulerRef,
    containerRef,
    dividerRef,
    dividerWidth: DIVIDER_WIDTH,
    isFullscreened,
  });

  const [leftChild, rightChild] = React.Children.toArray(children);

  const maxHeightWithUnit =
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <>
      <div ref={rulerRef} />
      <Wrapper style={{ flex: isFullscreened ? 1 : undefined }}>
        <Container
          className={className}
          ref={containerRef}
          style={{ '--max-height': maxHeightWithUnit }}
        >
          <FirstPane style={{ '--desktop-flex': leftWidth }}>
            {leftChild}
          </FirstPane>
          <Divider ref={dividerRef}>
            <VisuallyHidden>
              Resize editor. Use left/right arrows.
            </VisuallyHidden>
          </Divider>
          <SecondPane
            style={{
              '--desktop-flex': rightWidth,
            }}
          >
            {rightChild}
          </SecondPane>
        </Container>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  max-width: 100%;
  overflow: hidden;
  flex: 1;
  container-type: inline-size;

  @media ${BREAKPOINTS.mdAndSmaller} {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  @container (min-width: ${SPLIT_BREAKPOINT}rem) {
    flex-direction: row;
    align-items: stretch;
  }
`;

const Pane = styled.div`
  @container (min-width: ${SPLIT_BREAKPOINT}rem) {
    flex: var(--desktop-flex);
    min-width: 200px;
  }
`;

const FirstPane = styled(Pane)`
  /* On mobile, this one should grow/shrink as needed. */
  flex: 1;
  min-height: 200px;

  @container (min-width: ${SPLIT_BREAKPOINT}rem) {
    margin-right: -8px;

    flex: var(--desktop-flex);
    min-height: unset;
  }
`;
const SecondPane = styled(Pane)`
  border-top: 1px solid var(--color-gray-100);

  @container (min-width: ${SPLIT_BREAKPOINT}rem) {
    margin-left: -8px;
    border-top: none;
  }
`;

const Divider = styled.button`
  display: none;

  @container (min-width: ${SPLIT_BREAKPOINT}rem) {
    display: block;
    position: relative;
    z-index: 2;
    pointer-events: auto;
    width: ${DIVIDER_WIDTH}px;
    cursor: col-resize;
    padding: 0;

    &:before,
    &:after {
      content: '';
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;
    }

    &:before {
      width: 1px;
      height: auto;
      background-color: var(--color-gray-100);
    }

    &:after {
      width: 11px;
      background-color: hsl(210deg 15% 20% / 0.5);
      opacity: 0;
      transform: scaleX(0);
      transition:
        opacity 500ms,
        transform 500ms;
    }

    &:hover:after {
      opacity: 1;
      transform: scaleX(1);
      transition:
        opacity 250ms,
        transform 500ms cubic-bezier(0.17, 0.67, 0.44, 1);
    }
  }
`;

export default SplitPane;
